// @ts-strict-ignore
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Box, Button, Callout, Checkbox, Input, Radio, Sheet } from '@node-space/ui-components'
import { type AccountUser, type AccountUserGroup } from 'types/accounts'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { Amp } from 'utils/tracker'
import { useTeamMembersForm } from '../hooks/useTeamMembersForm'

type TeamMemberEditorProps = {
  accountUser?: AccountUser
  userGroups?: AccountUserGroup[]
  action: 'EDIT' | 'CREATE'
  canManageApprover?: boolean
  handleCloseModal: () => void
  getValues: () => void
}

export const TeamMemberEditor = ({
  accountUser,
  userGroups,
  action,
  canManageApprover,
  handleCloseModal,
  getValues,
}: TeamMemberEditorProps) => {
  const { t } = useTranslation()

  const existingUser = accountUser?.groups?.[0]?.id?.toString() || ''

  const {
    formData,
    formErrors,
    errorMessage,
    selectedRadioOption,
    radioOptions,
    isLoading,
    onRadioChange,
    onSubmit,
    handleSubmit,
    setValue,
    trigger,
  } = useTeamMembersForm({
    accountUser,
    existingUser,
    userGroups,
    action,
    canManageApprover,
    handleCloseModal,
    getValues,
  })

  useEffect(() => {
    if (!existingUser) {
      Amp.track(AmplitudeEvent.ADD_TEAM_MEMBER_INITIATE, {
        category: AmplitudeEventCategory.APPLICATION,
        action: AmplitudeEventAction.CLICK,
      })
    }
    if (existingUser) {
      Amp.track(AmplitudeEvent.EDIT_TEAM_MEMBER_INITIATE, {
        category: AmplitudeEventCategory.APPLICATION,
        action: AmplitudeEventAction.CLICK,
      })
    }
  }, [])

  return (
    <Sheet
      isOpen
      onClose={handleCloseModal}
      header={action === 'EDIT' ? t('editTeamMember') : t('createTeamMember')}
    >
      <Box
        tag="form"
        flexCol
        gap={24}
        onSubmit={handleSubmit(onSubmit)}
        testId="team-member-editor-form"
      >
        <Input
          label={t('teamMemberEmailAddress')}
          placeholder={t('teamMemberEmailAddressPlaceholder')}
          name="emailAddress"
          testId="email-address"
          disabled={!!accountUser}
          value={formData.emailAddress || ''}
          error={!!formErrors.emailAddress}
          errorText={formErrors.emailAddress?.message}
          onChange={e => {
            setValue('emailAddress', e.target.value)
          }}
          onBlur={() => trigger('emailAddress')}
        />
        <Box>
          <Radio
            name="groupOne"
            onChange={onRadioChange}
            options={radioOptions}
            value={selectedRadioOption}
            horizontal={false}
            border={false}
          />
          {action === 'CREATE' && canManageApprover && (
            <Box flex paddingX={12} paddingY={16}>
              <Checkbox
                id="team-member-approver-checkbox"
                name="team-member-approver-checkbox"
                testId="team-member-approver-checkbox"
                checked={formData.approver}
                label={t('teamMember.makeUserAnApprover')}
                onCheckedChange={value => setValue('approver', Boolean(value))}
              >
                {t('teamMember.makeUserAnApproverDescription')}
              </Checkbox>
            </Box>
          )}
        </Box>

        {formErrors.groups && (
          <span className="text-body-regular text-foreground-action-destructive">
            {formErrors.groups.message}
          </span>
        )}
        {!!errorMessage && <Callout variant="error">{errorMessage}</Callout>}

        <Callout variant="info">
          <span>
            {t('teamMember.permissionsCalloutPartOne')}{' '}
            <Link className="underline" to={process.env.ROLES_FAQ}>
              {t('teamMember.permissionsCalloutPartTwo')}
            </Link>{' '}
            {t('teamMember.permissionsCalloutPartThree')}
          </span>
        </Callout>

        <Box flex justify="between">
          <Button onClick={handleCloseModal} variant="secondary" type="button">
            {t('cancel')}
          </Button>
          <Button type="submit" testId="submit-button" loading={isLoading}>
            {action === 'EDIT' ? t('save') : t('sendTeamMemberInvite')}
          </Button>
        </Box>
      </Box>
    </Sheet>
  )
}
